import React from 'react';
import { Helmet } from 'react-helmet-async';
import HighlightText from '../components/HighlightText';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms & Conditions | MyHomeTutors Lucknow',
  description: 'Read the Terms and Conditions of MyHomeTutors Lucknow for students, parents, and tutors using our home tutoring platform.',
  url: 'https://myhometutors.org/terms-and-conditions',
  publisher: {
    '@type': 'Organization',
    name: 'MyHomeTutors',
    telephone: '+919415905584',
  },
};

const sections = [
  {
    title: '1. Acceptance of Terms',
    content: [
      'By accessing or using the MyHomeTutors website (myhometutors.org) or any of our services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.',
      'These terms apply to all users of the platform including students, parents, and tutors.',
    ],
  },
  {
    title: '2. Services Provided',
    content: [
      'MyHomeTutors provides a platform that connects students and parents with verified home tutors in Lucknow. Our services include:',
      '• Tutor discovery and matching based on subject, class, location, and gender preference.',
      '• Tutor profile listing and verification services for educators.',
      '• Free demo class facilitation between students and tutors.',
      '• Ongoing support and tutor replacement services.',
      'MyHomeTutors acts as an intermediary and is not directly responsible for the quality of individual tutoring sessions unless stated otherwise.',
    ],
  },
  {
    title: '3. User Responsibilities – Students & Parents',
    content: [
      'As a student or parent using our platform, you agree to:',
      '• Provide accurate information during the inquiry or registration process.',
      '• Treat tutors with respect and maintain a professional relationship.',
      '• Inform us promptly if you are unsatisfied with a tutor so we can arrange a replacement.',
      '• Not share tutor contact details with third parties for purposes outside our platform.',
      '• Pay agreed tuition fees directly to tutors as per the mutually decided schedule.',
    ],
  },
  {
    title: '4. User Responsibilities – Tutors',
    content: [
      'As a tutor registered on MyHomeTutors, you agree to:',
      '• Provide accurate qualifications, experience, and identity documents during registration.',
      '• Maintain professional conduct and punctuality in all tutoring sessions.',
      '• Not misrepresent your expertise or qualifications to students or parents.',
      '• Inform MyHomeTutors if you are unable to continue with a student.',
      '• Comply with our background verification process and any updates thereto.',
    ],
  },
  {
    title: '5. Demo Class Policy',
    content: [
      'MyHomeTutors offers a free demo class to help students evaluate a tutor before committing. The following conditions apply:',
      '• One free demo class is provided per student-tutor match.',
      '• The demo class is typically 45–60 minutes in duration.',
      '• If you are unsatisfied after the demo, we will arrange an alternative tutor at no charge.',
      '• Repeated misuse of the free demo class policy may result in restrictions on future requests.',
    ],
  },
  {
    title: '6. Tutor Replacement Policy',
    content: [
      'We want every student to have the best possible tutoring experience. If you are not satisfied with your assigned tutor:',
      '• Contact us within the first 7 days of sessions beginning.',
      '• We will arrange a replacement tutor at no additional cost.',
      '• Replacement requests after the initial period will be evaluated on a case-by-case basis.',
    ],
  },
  {
    title: '7. Payment Terms',
    content: [
      'All tuition fee arrangements are made directly between the student/parent and the tutor. MyHomeTutors does not process payments on behalf of tutors. However:',
      '• A nominal service/registration fee may be charged to tutors for profile listing (details communicated at registration).',
      '• MyHomeTutors is not liable for any payment disputes between students and tutors.',
      '• We recommend confirming all fee arrangements in writing before sessions begin.',
    ],
  },
  {
    title: '8. Intellectual Property',
    content: [
      'All content on the MyHomeTutors website — including text, graphics, logos, tutor profiles, and design — is the property of MyHomeTutors and is protected by applicable intellectual property laws.',
      'You may not reproduce, distribute, or use our content for commercial purposes without prior written permission.',
    ],
  },
  {
    title: '9. Limitation of Liability',
    content: [
      'MyHomeTutors is a facilitating platform and shall not be held liable for:',
      '• Academic results or outcomes of any student.',
      '• Conduct or behaviour of tutors during or outside sessions.',
      '• Any disputes arising from the tutor-student relationship.',
      '• Technical interruptions or downtime of the website.',
      'We strongly recommend parents to conduct their own assessment during the free demo class.',
    ],
  },
  {
    title: '10. Termination',
    content: [
      'MyHomeTutors reserves the right to suspend or terminate any user account or tutor profile if:',
      '• False information has been provided during registration.',
      '• There are reports of misconduct, harassment, or unprofessional behaviour.',
      '• Terms and Conditions are violated repeatedly.',
      'Terminated users may not re-register without prior approval from our team.',
    ],
  },
  {
    title: '11. Changes to Terms',
    content: [
      'MyHomeTutors may update these Terms and Conditions at any time. We will notify users of significant changes by posting the updated terms on this page. Continued use of our services after changes are posted constitutes your acceptance.',
    ],
  },
  {
    title: '12. Governing Law',
    content: [
      'These Terms and Conditions are governed by the laws of India. Any disputes arising from the use of our services shall be subject to the exclusive jurisdiction of the courts in Lucknow, Uttar Pradesh.',
    ],
  },
  {
    title: '13. Contact Us',
    content: [
      'For any questions regarding these Terms and Conditions, please contact us:',
      '📍 H.N 1/358, Vastu Khand, Gomti Nagar, Lucknow, Uttar Pradesh 226010',
      '📞 +91 9415905584',
    ],
  },
];

const TermsPage = () => (
  <div className="min-h-screen bg-[#fdf8f4]">
    <Helmet>
      <title>Terms & Conditions | MyHomeTutors Lucknow</title>
      <meta name="description" content="Read the Terms and Conditions of MyHomeTutors Lucknow. Understand your rights and responsibilities as a student, parent, or tutor on our platform." />
      <link rel="canonical" href="https://myhometutors.org/terms-and-conditions/" />
      <meta property="og:title" content="Terms & Conditions | MyHomeTutors Lucknow" />
      <meta property="og:description" content="Read the Terms and Conditions of MyHomeTutors Lucknow." />
      <meta property="og:url" content="https://myhometutors.org/terms-and-conditions" />
      <meta property="og:type" content="website" />
    </Helmet>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

    {/* Header */}
    <section className="bg-gradient-to-br from-[#fff5f5] to-[#ffeaea] py-14 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle,#fd6c6b 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
          Terms & <HighlightText>Conditions</HighlightText>
        </h1>
        <p className="text-gray-600 font-medium text-lg">Last updated: January 2025</p>
        <p className="text-gray-500 font-medium mt-3 max-w-2xl mx-auto">
          Please read these Terms and Conditions carefully before using MyHomeTutors. These terms govern your use of our home tutoring platform.
        </p>
      </div>
    </section>

    {/* Content */}
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      {/* Quick Nav */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
        <h3 className="font-extrabold text-slate-900 mb-4 text-sm uppercase tracking-wider">Quick Navigation</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sections.slice(0, 9).map((s, i) => (
            <span key={i} className="text-xs font-semibold text-gray-500 bg-gray-50 px-3 py-2 rounded-lg truncate">
              {s.title}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 md:p-12 space-y-10">
          {sections.map((sec, i) => (
            <div key={i} className="pb-10 border-b border-gray-100 last:border-0 last:pb-0">
              <h2 className="text-xl font-extrabold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center text-primary text-xs font-extrabold flex-shrink-0">
                  {i + 1}
                </span>
                {sec.title.replace(/^\d+\.\s/, '')}
              </h2>
              <div className="space-y-2.5">
                {sec.content.map((line, j) => (
                  <p key={j} className={`font-medium leading-relaxed ${line.startsWith('•') ? 'text-gray-600 pl-4' : 'text-gray-600'}`}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 bg-red-50 border border-red-100 rounded-2xl p-6 text-center">
        <p className="text-sm font-bold text-slate-700">Have questions about our Terms?</p>
        <p className="text-sm text-gray-500 font-medium mt-1">
          Contact us at <a href="tel:+919415905584" className="text-primary font-bold hover:underline">+91 9415905584</a>
        </p>
      </div>
    </section>
  </div>
);

export default TermsPage;